import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {take, takeUntil} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {DiagnosisService} from '../../../../services/api/diagnosis.service';
import {EmailService} from '../../../../services/api/administration/email.service';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {FormSubmitService} from '../../../../services/form-submit.service';
import {
  TimeTrackingAbsenceRequestService
} from '../../../../services/api/time-tracking/time-tracking-absence-request.service';
import {
  DateTimeSelectorComponent,
  TIME_MODE
} from '../../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {DateTime} from 'luxon';
import {ReadOnlyStaticComponent} from '../../../../components/form-inputs/read-only-static/read-only-static.component';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {ConfirmDeleteModalComponent} from '../../../../components/confirm-delete-modal/confirm-delete-modal.component';
import {ToastrService} from 'ngx-toastr';
import TimeTrackingAbsenceRequestData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingAbsenceRequestData;
import TimeTrackingAbsenceRequest = Backend.App.Resources.Dashboard.TimeTracking.TimeTrackingAbsenceRequest;
import TimeTrackingAbsenceRequestProcessData = Backend.App.Data.Dashboard.TimeTracking.TimeTrackingAbsenceRequestProcessData;
import TimeTrackingAbsenceRequestStateEnum = Backend.App.Enums.TimeTracking.TimeTrackingAbsenceRequestStateEnum;

@Component({
  selector: 'app-authenticated-time-tracking-absence-requests-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    DateTimeSelectorComponent,
    FormGroupComponent,
    ReadOnlyStaticComponent,
    ModalModule,
  ],
  providers: [BsModalService],
  standalone: true,
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public absenceRequest?: TimeTrackingAbsenceRequest;
  public mode = EditModeEnum.CREATE;
  public showTimeTrackingEmployee = true;

  public form: StrictFormGroup<TimeTrackingAbsenceRequestData> = this.fb.group({
    start_at: this.fb.control('', [Validators.required]),
    half_day_start: this.fb.control(false),
    end_at: this.fb.control('', [Validators.required]),
    half_day_end: this.fb.control(false),
    note_employee: this.fb.control(''),
  });

  public processForm: StrictFormGroup<TimeTrackingAbsenceRequestProcessData> = this.fb.group({
    state: this.fb.control(TimeTrackingAbsenceRequestStateEnum.OPEN, [Validators.required]),
    note_team_leader_hr: this.fb.control(''),
  });

  protected readonly TIME_MODE = TIME_MODE;
  protected readonly TimeTrackingAbsenceRequestStateEnum = TimeTrackingAbsenceRequestStateEnum;
  protected readonly EditModeEnum = EditModeEnum;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    protected readonly diagnosisService: DiagnosisService,
    protected emailService: EmailService,
    private readonly formSubmitService: FormSubmitService,
    private readonly router: Router,
    private readonly timeTrackingAbsenceRequestService: TimeTrackingAbsenceRequestService,
    private readonly bsModalService: BsModalService,
    private readonly toastrService: ToastrService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const absenceRequest: TimeTrackingAbsenceRequest | undefined = data['absenceRequest'];
      this.showTimeTrackingEmployee = data['showTimeTrackingEmployee'] ?? false;

      if (absenceRequest) {
        this.absenceRequest = absenceRequest;
        this.mode = EditModeEnum.EDIT;

        this.form.patchValue(absenceRequest);
        this.processForm.patchValue(absenceRequest);
        this.form.disable();
      }

      this.reloadAbsenceRequest();

      this.titleService.setTitle(`authenticated.time_tracking.absence_requests.${this.mode}.title`);
      this.cdr.markForCheck();
    });
  }

  public cancel() {
    if (this.mode !== EditModeEnum.EDIT || !this.absenceRequest?.actions.cancel) return;

    this.bsModalService.show(ConfirmDeleteModalComponent<TimeTrackingAbsenceRequest>, {
      initialState: {
        modelId: this.absenceRequest.id,
        dialogTranslation: this.translate.instant('authenticated.time_tracking.absence_requests.edit.cancel.confirm_title'),
        successTranslation: this.translate.instant('authenticated.time_tracking.absence_requests.edit.cancel.success'),
        errorTranslation: this.translate.instant('authenticated.time_tracking.absence_requests.edit.cancel.error'),
        actionButtonTranslation: this.translate.instant('authenticated.time_tracking.absence_requests.edit.cancel.btn'),
        permissions: [PERMISSION.TIMETRACKING_TIMETRACKINGABSENCEREQUEST_UPDATE_ALL],
        observable: this.timeTrackingAbsenceRequestService.cancel(this.absenceRequest, {}),
      }
    }).content?.deleteSuccess.pipe(
      takeUntil(this.onDestroy$),
      take(1)
    ).subscribe(() => this.reloadAbsenceRequest());
  }

  public save() {
    this.formSubmitService.submit<TimeTrackingAbsenceRequest, TimeTrackingAbsenceRequestData, TimeTrackingAbsenceRequestService>({
      form: this.form,
      service: this.timeTrackingAbsenceRequestService,
      modelId: this.absenceRequest?.id,
      createPermission: PERMISSION.TIMETRACKING_TIMETRACKINGABSENCEREQUEST_CREATE_ALL,
      createSuccessMessage: this.translate.instant('authenticated.time_tracking.absence_requests.create.success'),
      createErrorMessage: this.translate.instant('authenticated.time_tracking.absence_requests.create.error'),
      dataTransformer: (data) => {
        return {
          start_at: DateTime.fromISO(data.start_at).toJSDate().toISOString(),
          end_at: DateTime.fromISO(data.end_at).toJSDate().toISOString(),
          half_day_start: data.half_day_start,
          half_day_end: data.half_day_end,
          note_employee: data.note_employee,
        };
      }
    }).subscribe(absenceRequest => {
      if (!absenceRequest) return;
      this.absenceRequest = absenceRequest;

      if (this.mode === EditModeEnum.CREATE) {
        this.router.navigateByUrl(`/time-tracking/absence-requests/personal/${this.absenceRequest.id}`).then();
      }

      this.mode = this.absenceRequest?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
      this.cdr.markForCheck();
    })
  }

  protected canProcess() {
    return this.absenceRequest?.actions.process;
  }

  protected submitProcessForm(state: TimeTrackingAbsenceRequestStateEnum.APPROVED | TimeTrackingAbsenceRequestStateEnum.REJECTED) {
    if (!this.absenceRequest || !this.canProcess()) return;
    this.processForm.controls.state.setValue(state);
    this.timeTrackingAbsenceRequestService.process(this.absenceRequest, this.processForm.value as TimeTrackingAbsenceRequestProcessData)
      .subscribe({
        next: res => {
          this.absenceRequest = res.data;

          this.processForm.patchValue(this.absenceRequest);
          this.processForm.markAsUntouched();
          this.processForm.markAsPristine();
          this.processForm.disable();

          this.toastrService.success(this.translate.instant(`authenticated.time_tracking.absence_requests.edit.${state}.success`));
          this.router.navigateByUrl(`/time-tracking/absence-requests/absences/${this.absenceRequest.id}`).then();

          this.cdr.markForCheck();
        },
        error: () => {
          this.toastrService.error(this.translate.instant(`authenticated.time_tracking.absence_requests.edit.${state}.error`));
        },
        complete: () => this.reloadAbsenceRequest(),
      });
  }

  private reloadAbsenceRequest() {
    if (!this.absenceRequest) return;

    this.timeTrackingAbsenceRequestService.get(this.absenceRequest.id, true)
      .subscribe(res => {
        this.absenceRequest = res.data;
        if (!this.canProcess()) {
          this.processForm.disable();
        }
        this.cdr.markForCheck();
      })
  }
}
